import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, KeyRound, X, HandCoins, Clock, CheckCircle2, Info, Building2 } from "lucide-react";
import { StatementView, StatementMissing } from "@/components/StatementView";
import { alpha, readableOn, themeOf, safeAccent } from "@/lib/portalTheme";
import useWhiteLabelHead from "@/lib/useWhiteLabelHead";

const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-semibold transition-transform hover:-translate-y-0.5";
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cardOf = (t) => ({ background: t.card, border: `1px solid ${t.border}`, borderRadius: 20 });

/* Asking the adviser to pay out what's waiting. */
const PayoutBox = ({ d, t, onAsked }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const req = d.payout_request;
  const accent = safeAccent(d.brand?.accent || "#4060FF", d.brand?.theme);
  const onAccent = readableOn(accent);
  const waiting = d.totals.payable ?? d.totals.outstanding;
  const accruing = d.totals.accruing || 0;
  const held = d.totals.at_maturity || 0;
  const maturity = (d.commitments || []).map((c) => c.maturity).filter(Boolean).sort()[0] || null;

  const ask = async () => {
    setBusy(true);
    try {
      await api.post("/client/payout-request", { note });
      toast.success("Your adviser has been told");
      setOpen(false); setNote("");
      onAsked();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (req?.status === "open") {
    return (
      <div className="p-4 flex items-start gap-3" style={cardOf(t)} data-testid="payout-pending">
        <Clock size={15} style={{ color: accent }} className="mt-0.5 shrink-0" />
        <div className="text-sm" style={{ color: t.ink2 }}>
          <b style={{ color: t.ink }}>You asked to be paid {usd(req.amount)}</b> on {String(req.created_at).slice(0, 10)}.
          Your adviser has been told and will be in touch.
        </div>
      </div>
    );
  }

  return (
    <>
      {req?.status === "settled" && (
        <div className="p-4 flex items-start gap-3" style={{ ...cardOf(t), background: alpha("#00863A", 0.08) }} data-testid="payout-settled">
          <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: "#00863A" }} />
          <div className="text-sm" style={{ color: t.ink2 }}>
            <b style={{ color: t.ink }}>{usd(req.amount)} marked as paid</b> on {String(req.settled_at).slice(0, 10)}.
            {req.reply ? ` Your adviser added: “${req.reply}”` : ""}
          </div>
        </div>
      )}
      {req?.status === "declined" && (
        <div className="p-4 flex items-start gap-3" style={cardOf(t)} data-testid="payout-declined">
          <Info size={15} className="mt-0.5 shrink-0" style={{ color: t.ink3 }} />
          <div className="text-sm" style={{ color: t.ink2 }}>
            Your last request wasn't actioned.{req.reply ? ` Your adviser said: “${req.reply}”` : " Your adviser will be in touch."}
          </div>
        </div>
      )}
      {waiting > 0 && (
        <div className="p-4" style={cardOf(t)} data-testid="payout-ask-box">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm flex-1 min-w-[220px]" style={{ color: t.ink2 }}>
              <b style={{ color: t.ink }}>{usd(waiting)} is ready to be paid.</b> Ask your adviser to send it on whenever you're ready.
              {accruing > 0 && <span style={{ color: t.ink3 }}> This month's {usd(accruing)} is still running and can be paid once the month closes.</span>}
            </div>
            {!open && (
              <button type="button" onClick={() => setOpen(true)} className={btn}
                style={{ background: accent, color: onAccent }} data-testid="payout-ask">
                <HandCoins size={13} /> Ask to be paid
              </button>
            )}
          </div>
          {open && (
            <div className="mt-3">
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Anything you'd like your adviser to know (optional)"
                className="w-full px-3 py-2 rounded-xl outline-none text-sm"
                style={{ background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.ink }}
                data-testid="payout-note" />
              <div className="flex items-center gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} disabled={busy} className={btn}
                  style={{ background: t.band, color: t.ink2 }}>Cancel</button>
                <button type="button" onClick={ask} disabled={busy} className={btn}
                  style={{ background: accent, color: onAccent }} data-testid="payout-send">
                  {busy ? "Sending…" : `Request ${usd(waiting)}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {waiting <= 0 && held > 0 && !req && (
        <div className="p-4 flex items-start gap-3" style={cardOf(t)} data-testid="payout-at-maturity">
          <Clock size={15} className="mt-0.5 shrink-0" style={{ color: t.ink3 }} />
          <div className="text-sm" style={{ color: t.ink2 }}>
            <b style={{ color: t.ink }}>{usd(held)}</b> has built up and is held until your maturity date
            {maturity ? <> on <b style={{ color: t.ink }}>{maturity}</b></> : null} — that's the agreement you signed.
            Your adviser will be in touch as the date comes up.
          </div>
        </div>
      )}
      {waiting <= 0 && held <= 0 && accruing > 0 && !req && (
        <div className="p-4 flex items-start gap-3" style={cardOf(t)} data-testid="payout-accruing-only">
          <Clock size={15} className="mt-0.5 shrink-0" style={{ color: t.ink3 }} />
          <div className="text-sm" style={{ color: t.ink2 }}>
            This month's <b style={{ color: t.ink }}>{usd(accruing)}</b> is still running. Once the month closes you'll be able to
            ask your adviser to pay it out.
          </div>
        </div>
      )}
    </>
  );
};

/* First sign-in on an adviser-issued password: they must choose their own. */
const ForcedPassword = ({ brand, onDone }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const t = themeOf(brand?.theme);
  const accent = safeAccent(brand?.accent || "#4060FF", brand?.theme);
  const onAccent = readableOn(accent);

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/client/change-password", { password: pw.trim() });
      toast.success("Password set — that's the one to use from now on");
      onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not set that password");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: brand?.theme === "dark" ? "rgba(6,9,16,0.82)" : "rgba(15,18,38,0.72)", backdropFilter: "blur(10px)" }} data-testid="forced-password">
      <div className="p-7 w-full max-w-md rounded-3xl border" style={{ background: t.cardStrong, borderColor: t.border, backdropFilter: "blur(16px)" }}>
        <div className="flex items-center gap-2.5 mb-5">
          {brand?.logo
            ? <img src={brand.logo} alt="" style={{ height: 30, objectFit: "contain" }} />
            : <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: alpha(accent, 0.14), color: accent }}><Building2 size={16} /></span>}
          <div className="text-sm font-semibold" style={{ color: t.ink }}>{brand?.name}</div>
        </div>
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: alpha(accent, 0.14) }}>
          <KeyRound size={19} style={{ color: accent }} />
        </div>
        <h2 className="font-display text-2xl font-semibold tracking-tight" style={{ color: t.ink }}>Choose your own password</h2>
        <p className="text-sm mt-2" style={{ color: t.ink2 }}>
          The password you were given was set by your adviser. Pick one only you know before you carry on.
        </p>
        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] mt-5 mb-1.5 block" style={{ color: t.ink3 }}>New password</label>
        <input type="text" value={pw} autoFocus disabled={busy}
          onChange={(e) => { setPw(e.target.value); setError(""); }}
          className="w-full px-3 py-2.5 rounded-xl border outline-none text-sm font-mono"
          style={{ borderColor: t.border, background: t.inputBg, color: t.ink }}
          data-testid="forced-password-input" />
        <div className="text-[11px] mt-1.5" style={{ color: t.ink3 }}>At least 8 characters with an uppercase letter, a number and a symbol.</div>
        {error && (
          <div className="mt-3 text-xs rounded-xl px-3 py-2" style={{ background: "rgba(220,38,38,0.12)", color: "#DC2626" }} data-testid="forced-password-error">{error}</div>
        )}
        <button type="button" onClick={save} disabled={busy || !pw.trim()}
          className="w-full mt-5 py-2.5 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-60"
          style={{ background: accent, color: onAccent }} data-testid="forced-password-submit">
          {busy ? "Saving…" : "Save and open my statement"}
        </button>
      </div>
    </div>
  );
};

/** The signed-in client portal — their statement, on their adviser's own site. */
export default function ClientPortal() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const [d, setD] = useState(null);
  const [gone, setGone] = useState(false);
  const [pw, setPw] = useState(null);

  const load = () => api.get("/client/me").then(({ data }) => setD(data)).catch(() => setGone(true));
  useEffect(() => { load(); }, []);
  useWhiteLabelHead(d?.brand);

  // The client's home is their adviser's address, never ours — /client and any
  // other firm's slug both bounce to the right one.
  useEffect(() => {
    const mine = d?.brand?.slug;
    if (mine && slug !== mine) navigate(`/p/${mine}/portal`, { replace: true });
  }, [d, slug, navigate]);

  const signOut = async () => {
    const mine = d?.brand?.slug;
    await logout();
    navigate(mine ? `/p/${mine}/login` : "/login", { replace: true });
  };

  const changePassword = async () => {
    setPw((s) => ({ ...s, busy: true, error: "" }));
    try {
      await api.post("/client/change-password", { password: pw.value });
      toast.success("Password changed — use it next time you sign in");
      setPw(null);
    } catch (err) {
      setPw((s) => ({ ...s, busy: false, error: formatApiError(err.response?.data?.detail) || "Could not change it" }));
    }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/client/statement.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement-${d.client.reference}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build your PDF just now"); }
  };

  if (gone) {
    return (
      <StatementMissing
        testid="client-portal-gone"
        title="We can't open your statement"
        body="Your adviser can sort this out for you."
      />
    );
  }
  if (!d) return <div className="min-h-screen flex items-center justify-center text-sm text-ink-2" data-testid="client-portal-loading">Loading your statement…</div>;

  if (d.must_change_password) {
    return <ForcedPassword brand={d.brand} onDone={async () => { await load(); refresh?.(); }} />;
  }

  const t = themeOf(d.brand?.theme);
  const accent = safeAccent(d.brand?.accent || "#4060FF", d.brand?.theme);
  const onAccent = readableOn(accent);

  return (
    <div data-testid="client-portal">
      <StatementView
        d={d}
        subtitle={`Signed in as ${user?.email || d.client.email}`}
        onDownload={downloadPdf}
        banner={<PayoutBox d={d} t={t} onAsked={load} />}
        actions={
          <>
            <button
              type="button"
              onClick={() => setPw({ value: "", busy: false, error: "" })}
              className={btn}
              style={{ background: alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.2), color: onAccent }}
              data-testid="client-change-password"
            >
              <KeyRound size={13} /> Password
            </button>
            <button
              type="button"
              onClick={signOut}
              className={btn}
              style={{ background: onAccent, color: accent }}
              data-testid="client-sign-out"
            >
              <LogOut size={13} /> Sign out
            </button>
          </>
        }
      />

      {pw && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(15,18,38,0.55)", backdropFilter: "blur(8px)" }}
          onClick={() => !pw.busy && setPw(null)} data-testid="client-pw-modal">
          <div className="p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}
            style={{ ...cardOf(t), background: t.cardStrong, backdropFilter: "blur(14px)" }}>
            <button type="button" onClick={() => !pw.busy && setPw(null)}
              className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: t.band, color: t.ink2 }} data-testid="client-pw-close">
              <X size={15} />
            </button>
            <div className="font-display text-lg font-semibold" style={{ color: t.ink }}>Choose a new password</div>
            <p className="text-[12.5px] mt-1 mb-3" style={{ color: t.ink2 }}>At least 8 characters with an uppercase letter, a number and a symbol.</p>
            <input
              type="text" value={pw.value} autoFocus
              onChange={(e) => setPw((s) => ({ ...s, value: e.target.value, error: "" }))}
              className="w-full px-3 py-2 rounded-xl outline-none text-sm font-mono"
              style={{ background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.ink }}
              data-testid="client-pw-input"
            />
            {pw.error && (
              <div className="text-[12px] mt-3 px-3 py-2 rounded-xl" style={{ background: alpha("#E11D48", 0.1), color: "#E11D48" }}
                data-testid="client-pw-error">{pw.error}</div>
            )}
            <button type="button" onClick={changePassword} disabled={pw.busy || !pw.value.trim()}
              className={`${btn} w-full mt-4 justify-center !py-2.5 disabled:opacity-50`}
              style={{ background: accent, color: onAccent }} data-testid="client-pw-submit">
              {pw.busy ? "Saving…" : "Save password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
